// src/pages/ProductPage.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeemed, setRedeemed] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    api.getProduct(id)
      .then(async p => {
        setProduct(p);
        api.trackEvent('PRODUCT_VIEW', p.storeId, p.id);
        const s = await api.getStore(p.storeId);
        setStore(s);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const openMaps = () => {
    if (!store) return;
    const query = encodeURIComponent(`${store.name}, ${store.mall?.name || ''}`);
    const url = /iPhone|iPad|iPod/i.test(navigator.userAgent)
      ? `maps://maps.apple.com/?q=${query}&ll=${store.latitude},${store.longitude}`
      : `https://www.google.com/maps/search/?api=1&query=${store.latitude},${store.longitude}`;
    window.open(url, '_blank');
  };

  const redeemCoupon = async (couponId) => {
    if (!user) { navigate('/auth?redirect=' + encodeURIComponent(window.location.pathname)); return; }
    setRedeeming(true);
    try {
      await api.redeemCoupon(couponId);
      api.trackEvent('DEAL_LOCK', product.storeId, product.id, couponId);
      setRedeemed(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;
  if (!product) return <div className="page-error">Product not found.</div>;

  const activeCoupons = store?.coupons?.filter(c => c.productId === product.id || !c.productId) || [];

  return (
    <div className="detail-root">
      <div className="detail-hero" style={{ backgroundImage: product.imageUrl ? `url(${product.imageUrl})` : undefined }}>
        {!product.imageUrl && <div className="detail-hero-placeholder">📦</div>}
        <div className="detail-hero-top">
          <button className="detail-back-btn" onClick={() => navigate(-1)}>←</button>
        </div>
      </div>

      <div className="detail-body">
        <div className="detail-title-row">
          <div>
            <h1 className="detail-name">{product.name}</h1>
            <p className="detail-price">₹{product.price.toLocaleString()}</p>
          </div>
        </div>

        {product.description && <p className="detail-desc">{product.description}</p>}

        {/* Store info */}
        {store && (
          <div className="detail-store-row" onClick={() => navigate(`/store/${store.id}`)}>
            <div className="detail-store-img" style={{ backgroundImage: store.imageUrl ? `url(${store.imageUrl})` : undefined }}>
              {!store.imageUrl && '🏪'}
            </div>
            <div className="detail-store-info">
              <p className="detail-store-name">{store.name}</p>
              <p className="detail-store-meta">Floor {store.floorNumber ?? '–'} · Unit {store.unitNumber ?? '–'}</p>
            </div>
            <span className="detail-arrow">›</span>
          </div>
        )}

        {/* Get Directions */}
        <button className="directions-btn" onClick={openMaps}>
          <span>📍</span> Get Directions
        </button>

        {/* Active Coupons */}
        {activeCoupons.length > 0 && (
          <div className="detail-coupons">
            <p className="detail-coupons-title">Available Deals</p>
            {activeCoupons.map(coupon => {
              const discount = coupon.discountType === 'PERCENTAGE'
                ? `${coupon.discountValue}% OFF`
                : `₹${coupon.discountValue} OFF`;
              return (
                <div key={coupon.id} className="detail-coupon-card">
                  <div className="detail-coupon-left">
                    <p className="detail-coupon-discount">{discount}</p>
                    <p className="detail-coupon-title">{coupon.title}</p>
                    <p className="detail-coupon-expiry">
                      Until {new Date(coupon.validUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </p>
                  </div>
                  <button
                    className={`lock-deal-btn ${redeemed ? 'locked' : ''}`}
                    onClick={() => redeemCoupon(coupon.id)}
                    disabled={redeemed || redeeming}
                  >
                    {redeemed ? '✓ Locked' : redeeming ? '…' : user ? 'Lock Deal' : 'Sign in to Lock'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
